import { zip } from 'lodash';
import { v4 as uuidv4 } from 'uuid';
import { isBlobUrl } from './stringUtil';


const isSameFile = async (file1: File, file2: File): Promise<boolean> => {
    if(file1.size !== file2.size) {
        return Promise.resolve(false);
    }
    return Promise.all([
        file1.arrayBuffer(),
        file2.arrayBuffer()
    ]).then(([buf1, buf2]) => {
        const arr1 = new Uint8Array(buf1);
        const arr2 = new Uint8Array(buf2);
        return zip(arr1, arr2).every(([val1, val2]) => val1 === val2);
    });
};

const addUUIDtoFileName = (file: File) => {
    const newName = file.name.split(".").slice(0, -1).join(".") + "-" + uuidv4() + "." + file.name.split(".").slice(-1)[0];
    return new File([file], newName, { type: file.type });
};

export const refineSameNameFiles = async (files: { [blobUrl: string]: File }): Promise<{ [blobUrl: string]: File }> => {
    const refinedFiles: {[blobUrl: string]: File} = {};
    const distinctFiles: File[] = [];

    for (const [blobUrl, file] of Object.entries(files)) {
        const sameNameFiles = distinctFiles.filter(({ name: fileName }) => fileName === file.name);

        if (!sameNameFiles.length) {
            distinctFiles.push(file);
            refinedFiles[blobUrl] = file;
            continue;
        }
        let sameFile: File | null = null;
        for (const sameNameFile of sameNameFiles) {
            if (await isSameFile(file, sameNameFile)) {
                sameFile = sameNameFile;
                break;
            }
        }

        if (sameFile) {
            refinedFiles[blobUrl] = sameFile;
            continue;
        } else {
            distinctFiles.push(file);
            refinedFiles[blobUrl] = addUUIDtoFileName(file);
            continue;
        }
    }

    return refinedFiles;
};

export const replaceBlobUrlToFilenameAndGetNamedFiles = <T>(data: T, localUploadedFiles: { [blobUrl: string]: File }): { data: T, files: File[] } => {
    const filesToSubmit: { [blobUrl: string]: File } = {};

    const transformBlobUrl = <T>(data: T): T => {
        if (typeof data !== 'object' || data === null) {
            return data;
        }
        if (Array.isArray(data)) {
            return data.map(item => transformBlobUrl(item)) as T;
        }

        return Object.entries(data).reduce((acc, [key, value]) => {
            if (typeof value === 'string' && isBlobUrl(value) && localUploadedFiles[value]) {
                const file = localUploadedFiles[value];
                filesToSubmit[value] = file;
                acc[key as keyof T] = (file?.name ?? value) as T[keyof T];
            } else if (typeof value === 'object' && value !== null) {
                acc[key as keyof T] = transformBlobUrl(value);
            } else {
                acc[key as keyof T] = value;
            }
            return acc;
        }, {} as T);
    };

    return {
        data: transformBlobUrl(data),
        files: Object.values(filesToSubmit)
    };
};
